import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { authApi } from '@/api';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { useState } from 'react';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { AuthButton } from '@/core/auth/components/AuthButton';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Min 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (!token) {
      notify.error('Invalid reset link');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, newPassword: data.newPassword });
      notify.success('Password reset successful');
      navigate(ORG_SIGN_IN_PATH);
    } catch (err) {
      showApiError(err, 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title="Reset password">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <AuthInput
          label="New password"
          type="password"
          autoComplete="new-password"
          {...register('newPassword')}
          error={errors.newPassword?.message}
        />
        <AuthInput
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
        <AuthButton type="submit" loading={loading} disabled={!token}>
          Reset password
        </AuthButton>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Link to={ORG_SIGN_IN_PATH} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
          Back to login
        </Link>
      </p>
    </AuthCard>
  );
}
